import { BellRing, CircleCheck, CircleX, Clapperboard, Download, Loader2 } from "lucide-react";

import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";
import { Button } from "components/ui/button";
import { Dialog, DialogContent } from "components/ui/dialog";

/**
 * The "exporting -> download" modal for a caption transcript's burned-in
 * video export — a pure presentation of whatever job TranscriptEditorPage
 * is already tracking (enqueue/poll/retry all live there, same as
 * translation/review). No job yet (still POSTing) reads the same as a
 * pending job — both just show the spinner.
 */
export function VideoExportDialog({
  open,
  onOpenChange,
  job,
  downloading,
  downloadProgress,
  onDownload,
  onRetry,
}) {
  const { language } = useLanguage();
  const tr = translations[language].videoExport;
  const failed = job?.status === "failed";
  const completed = job?.status === "completed";
  const inFlight = !failed && !completed;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xs">
        {inFlight ? (
          <div className="flex flex-col items-center gap-6 px-2 py-8 text-center">
            <div className="relative flex h-20 w-20 items-center justify-center">
              <div className="absolute inset-0 rounded-full border-2 border-border" />
              <div
                className="absolute inset-0 animate-spin rounded-full border-2 border-transparent"
                style={{ borderTopColor: "hsl(var(--foreground) / 0.5)" }}
              />
              <Clapperboard className="size-7 text-muted-foreground" aria-hidden="true" />
            </div>
            <div className="grid gap-1.5">
              <p className="text-base font-semibold tracking-tight">{tr.rendering}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{tr.renderingDescription}</p>
            </div>
            <Button
              variant="outline"
              size="lg"
              className="w-full gap-2"
              onClick={() => onOpenChange(false)}
            >
              <BellRing className="size-4" aria-hidden="true" />
              {tr.notifyMeWhenReady}
            </Button>
          </div>
        ) : completed ? (
          <div className="flex flex-col items-center gap-6 px-2 py-8 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/10">
              <CircleCheck className="size-10 text-emerald-500" aria-hidden="true" />
            </div>
            <div className="grid gap-1.5">
              <p className="text-base font-semibold tracking-tight">{tr.ready}</p>
              <p className="text-sm text-muted-foreground">{tr.readyDescription}</p>
            </div>
            <div className="grid w-full gap-2">
              <Button variant="blue" size="lg" className="w-full gap-2" disabled={downloading} onClick={onDownload}>
                {downloading ? <Loader2 className="animate-spin" /> : <Download />}
                {downloading
                  ? Number.isFinite(downloadProgress)
                    ? `${tr.preparingDownload} ${downloadProgress}%`
                    : tr.preparingDownload
                  : tr.downloadVideo}
              </Button>
              {downloading && Number.isFinite(downloadProgress) ? (
                <div
                  role="progressbar"
                  aria-label={tr.downloadVideo}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={downloadProgress}
                  className="h-1 w-full overflow-hidden rounded-full bg-secondary"
                >
                  <div
                    className="h-full rounded-full bg-blue-500 transition-[width] duration-150"
                    style={{ width: `${downloadProgress}%` }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6 px-2 py-8 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
              <CircleX className="size-10 text-destructive" aria-hidden="true" />
            </div>
            <div className="grid gap-1.5">
              <p className="text-base font-semibold tracking-tight">{tr.failed}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {job?.failure_reason || tr.failedDescription}
              </p>
            </div>
            <Button variant="outline" size="lg" className="w-full" onClick={onRetry}>
              {tr.tryAgain}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
